import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './slideshow.css';
import { fetchMovies, mapMovieForCard } from '../api/starviewApi';
import { useCountry } from '../context/CountryContext';

const SLIDE_DURATION_MS = 7000;

const Slideshow = () => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const [movies, setMovies] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCurrentIndex(0);
    fetchMovies({ size: 6, country })
      .then((rawMovies) => {
        if (cancelled) return;
        setMovies(rawMovies.map(mapMovieForCard).filter((m) => m.image_url));
      })
      .catch((err) => console.error('Failed to load hero movies:', err));
    return () => {
      cancelled = true;
    };
  }, [country]);

  useEffect(() => {
    if (movies.length === 0 || paused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % movies.length);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(interval);
  }, [movies.length, paused]);

  const goTo = useCallback((idx) => {
    setCurrentIndex(idx);
  }, []);

  if (movies.length === 0) {
    return <div className="slideshow-container slideshow-loading" />;
  }

  return (
    <div
      className="slideshow-container"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {movies.map((movie, idx) => (
        <div
          key={movie.id}
          className={`slideshow-slide${currentIndex === idx ? ' active' : ''}`}
          style={{ backgroundImage: `url(${movie.image_url})` }}
        >
          <div className="slideshow-vignette" />
          <div className="slideshow-content">
            <span className="slideshow-eyebrow">{t('hero.nowPlaying')}</span>
            <h1 className="slideshow-title">{movie.title}</h1>
            <div className="slideshow-meta">
              {[movie.rating, movie.runtime, movie.genres].filter(Boolean).join('  •  ')}
            </div>
            <div className="slideshow-actions">
              <a href={`/Showtimes?movieId=${movie.id}`} className="slideshow-btn slideshow-btn-primary">
                {t('hero.getTickets')}
              </a>
              {movie.trailer_url && (
                <a
                  href={movie.trailer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="slideshow-btn slideshow-btn-ghost"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 2.5L13.5 8L4 13.5V2.5Z" fill="currentColor" />
                  </svg>
                  {t('hero.watchTrailer')}
                </a>
              )}
            </div>
          </div>
        </div>
      ))}

      <div className="slideshow-dots">
        {movies.map((movie, idx) => (
          <button
            key={movie.id}
            className={`slideshow-dot${currentIndex === idx ? ' active' : ''}`}
            aria-label={`Show ${movie.title}`}
            onClick={() => goTo(idx)}
          />
        ))}
      </div>

      <div className="slideshow-fade-bottom" />
    </div>
  );
};

export default Slideshow;
